import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import QuestionRenderer from "./QuestionRenderer";
import ConfirmDialog from "../common/ConfirmDialog";
import WizardProgressBar from "../wizard/WizardProgressBar";
import {
  getQuestionsForStep,
  getQuestionsByStep,
  getSortedSteps,
  checkQuestionVisibility,
} from "../../utils/questionUtils";
import "./AnswerForm.css";

const AnswerForm = (props) => {
  const {
    questions = [],
    questionTree = null,
    answers = {},
    onChange,
    onClear,
    showAllQuestions: initialShowAllQuestions = false,
    onNext,
    onPrevious,
    onComplete,
  } = props;

  const [localAnswers, setLocalAnswers] = useState(answers || {});
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [initializedQuestions, setInitializedQuestions] = useState(new Set());
  const [currentStep, setCurrentStep] = useState(0);
  const [showAllQuestions, setShowAllQuestions] = useState(
    initialShowAllQuestions
  );

  // Kullanıcı aktif yazarken answers prop sync'ini engelle
  const isUserTypingRef = useRef(false);
  const syncTimeoutRef = useRef(null);
  // onChange debounce için en güncel localAnswers'ı tut
  const localAnswersRef = useRef(localAnswers);

  // Generate temporary ID for new questions
  // Unmount'ta timer temizle (memory leak önleme)
  useEffect(() => {
    return () => {
      clearTimeout(syncTimeoutRef.current);
    };
  }, []);

  const generateTempId = useCallback(() => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Flatten questionTree to questions array
  // CRITICAL: Preserve nested questions in answers[].content.nestedQuestions
  // This is the same implementation as TemplateEditorPage for consistency
  const flattenQuestionTree = useCallback((tree) => {
    if (!tree || !Array.isArray(tree)) return [];
    
    const flattened = [];
    const questionMap = new Map(); // Map to store questions by their unique key
    
    // First pass: Collect all questions (including nested) and add to flattened array
    const collectQuestions = (nodes, parentId = null) => {
      nodes.forEach((node) => {
        const questionId = node._id ? (typeof node._id === 'string' ? node._id : node._id.toString()) : node._tempId;
        const _tempId = questionId || generateTempId();
        const uniqueKey = `${_tempId}_${parentId || 'root'}`;
        
        // Skip if already collected
        if (questionMap.has(uniqueKey)) {
          return;
        }
        
        const question = {
          ...node,
          _tempId: _tempId,
          parentId: parentId,
        };
        
        questionMap.set(uniqueKey, question);
        flattened.push(question);
        
        // Recursively collect nested questions from answers
        if (node.answers && Array.isArray(node.answers)) {
          node.answers.forEach((answer) => {
            if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
              collectQuestions(answer.content.nestedQuestions, _tempId);
            }
          });
        }
        
        // Collect children (for backward compatibility)
        if (node.children && Array.isArray(node.children)) {
          collectQuestions(node.children, _tempId);
        }
      });
    };
    
    collectQuestions(tree);
    
    // Second pass: Update questions with preserved answers and nestedQuestions references
    // CRITICAL: Use the original tree structure, not questionMap
    const updateQuestionFromTree = (node, question) => {
      if (node.answers && Array.isArray(node.answers)) {
        question.answers = node.answers.map((answer) => {
          const answerCopy = { ...answer };
          
          // Preserve nestedQuestions from answer.content.nestedQuestions
          if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
            // Map nested questions to their flattened versions
            const nestedQuestions = answer.content.nestedQuestions.map((nestedQ) => {
              const nestedId = nestedQ._id ? (typeof nestedQ._id === 'string' ? nestedQ._id : nestedQ._id.toString()) : nestedQ._tempId;
              const nestedTempId = nestedId || generateTempId();
              
              // Find the nested question in flattened array
              // Try multiple matching strategies
              const nestedQuestion = flattened.find(q => {
                // Match by _tempId and parentId
                if (q._tempId === nestedTempId && q.parentId === question._tempId) {
                  return true;
                }
                // Match by key and parentId (for cases where _id might be different)
                if (q.key === nestedQ.key && q.parentId === question._tempId) {
                  return true;
                }
                // Match by _id and parentId (if _id exists)
                if (nestedQ._id && q._id && q._id.toString() === nestedQ._id.toString() && q.parentId === question._tempId) {
                  return true;
                }
                return false;
              });
              
              if (nestedQuestion) {
                return {
                  ...nestedQuestion,
                  parentAnswerValue: answer.value,
                  _isNested: true,
                };
              }
              
              // If not found, return the nested question with proper metadata
              return {
                ...nestedQ,
                _tempId: nestedTempId,
                parentId: question._tempId,
                parentAnswerValue: answer.value,
                _isNested: true,
              };
            });
            
            answerCopy.content = {
              ...answer.content,
              nestedQuestions: nestedQuestions,
            };
          }
          
          return answerCopy;
        });
      }
    };
    
    // Traverse tree and update questions
    const traverseAndUpdate = (nodes) => {
      nodes.forEach((node) => {
        const questionId = node._id ? (typeof node._id === 'string' ? node._id : node._id.toString()) : node._tempId;
        const _tempId = questionId || generateTempId();
        
        // Find question in flattened array
        const question = flattened.find(q => 
          q._tempId === _tempId || 
          (q._id && node._id && q._id.toString() === node._id.toString()) ||
          (q.key === node.key && !q.parentId && !node.parentId)
        );
        
        if (question) {
          updateQuestionFromTree(node, question);
          
          // Recursively process nested questions
          if (node.answers && Array.isArray(node.answers)) {
            node.answers.forEach((answer) => {
              if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
                traverseAndUpdate(answer.content.nestedQuestions);
              }
            });
          }
        }
      });
    };
    
    traverseAndUpdate(tree);
    
    return flattened;
  }, [generateTempId]);

  const buildQuestionTreeFromFlat = useCallback((flatQuestions) => {
    if (
      !flatQuestions ||
      !Array.isArray(flatQuestions) ||
      flatQuestions.length === 0
    ) {
      return { tree: [], allFlat: [] };
    }

    const questionMap = new Map();
    // Helper function to get question key (handles ObjectId, string, _tempId, key)
    const getQuestionKey = (q) => {
      if (q._id) {
        return typeof q._id === 'string' ? q._id : q._id.toString();
      }
      if (q._tempId) {
        return q._tempId.toString();
      }
      return q.key;
    };
    
    flatQuestions.forEach((q) => {
      const key = getQuestionKey(q);
      questionMap.set(key, { ...q, children: [] });
    });

    const rootQuestions = [];
    flatQuestions.forEach((q) => {
      const key = getQuestionKey(q);
      const question = questionMap.get(key);

      if (q.parentId) {
        // parentId can be ObjectId, string, _tempId, or key
        const parentIdStr = q.parentId.toString();
        const parent = questionMap.get(parentIdStr) || 
                      Array.from(questionMap.values()).find(p => {
                        if (p._id) {
                          const pIdStr = typeof p._id === 'string' ? p._id : p._id.toString();
                          return pIdStr === parentIdStr;
                        }
                        if (p._tempId && p._tempId.toString() === parentIdStr) return true;
                        if (p.key === parentIdStr) return true;
                        return false;
                      });
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(question);
        }
      } else {
        rootQuestions.push(question);
      }

      if (q.answers && Array.isArray(q.answers)) {
        question.answers = q.answers.map((answer) => {
          const answerCopy = { ...answer };

          if (
            answer.content?.nestedQuestions &&
            Array.isArray(answer.content.nestedQuestions)
          ) {
            answerCopy.content = {
              ...answer.content,
              nestedQuestions: answer.content.nestedQuestions
                .map((nestedQ) => {
                  if (typeof nestedQ === "string") {
                    const found = questionMap.get(nestedQ) ||
                      Array.from(questionMap.values()).find(p => {
                        if (p._id && p._id.toString() === nestedQ) return true;
                        if (p._tempId && p._tempId.toString() === nestedQ) return true;
                        if (p.key === nestedQ) return true;
                        return false;
                      });
                    return found ? { ...found, _parentQuestion: question } : nestedQ;
                  }
                  const nestedKey = getQuestionKey(nestedQ);
                  const found = questionMap.get(nestedKey) ||
                    Array.from(questionMap.values()).find(p => {
                      if (nestedQ._id && p._id) {
                        const nIdStr = typeof nestedQ._id === 'string' ? nestedQ._id : nestedQ._id.toString();
                        const pIdStr = typeof p._id === 'string' ? p._id : p._id.toString();
                        if (pIdStr === nIdStr) return true;
                      }
                      if (nestedQ._tempId && p._tempId && p._tempId.toString() === nestedQ._tempId.toString()) return true;
                      if (nestedQ.key && p.key === nestedQ.key) return true;
                      return false;
                    });
                  return found ? { ...found, _parentQuestion: question } : nestedQ;
                })
                .filter(Boolean),
            };
          }

          return answerCopy;
        });
      }
    });

    // For LocationSelector questions with dependsOn: find parent and add _parentQuestion reference
    const allQuestionsList = Array.from(questionMap.values());
    allQuestionsList.forEach((q) => {
      if (q.dependsOn && q.type === 'LocationSelector') {
        // Find parent question by dependsOn ID
        // dependsOn can be: ObjectId (from backend), _id string, _tempId, or key
        const dependsOnStr = q.dependsOn.toString();
        
        // First try: direct lookup in questionMap (if dependsOn matches a question key)
        let parentQuestion = questionMap.get(dependsOnStr);
        
        // Second try: search all questions by various ID formats
        if (!parentQuestion) {
          parentQuestion = allQuestionsList.find(
            (p) => {
              const pKey = getQuestionKey(p);
              // Direct match by key
              if (pKey === dependsOnStr) return true;
              // Match by _id (ObjectId or string)
              if (p._id) {
                const pIdStr = typeof p._id === 'string' ? p._id : p._id.toString();
                if (pIdStr === dependsOnStr) return true;
              }
              // Match by _tempId
              if (p._tempId && p._tempId.toString() === dependsOnStr) return true;
              // Match by key (fallback)
              if (p.key === dependsOnStr) return true;
              return false;
            }
          );
        }
        
        if (parentQuestion) {
          q._parentQuestion = parentQuestion;
        }
      }
    });

    // Create flattened list with _parentQuestion references preserved
    // Start with all questions from questionMap (includes _parentQuestion references)
    const allFlat = Array.from(questionMap.values());
    
    // Also add nested questions from answers[].content.nestedQuestions
    // These already have _parentQuestion references set above
    allQuestionsList.forEach((q) => {
      if (q.answers && Array.isArray(q.answers)) {
        q.answers.forEach((answer) => {
          if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
            answer.content.nestedQuestions.forEach((nestedQ) => {
              // Only add if not already in allFlat (avoid duplicates)
              if (nestedQ && nestedQ.key && !allFlat.find(f => {
                const fKey = getQuestionKey(f);
                const nKey = getQuestionKey(nestedQ);
                return fKey === nKey;
              })) {
                allFlat.push(nestedQ);
              }
            });
          }
        });
      }
    });

    return { tree: rootQuestions, allFlat };
  }, []);

  const processedQuestionsData = useMemo(() => {
    // CRITICAL: Always prioritize questions over questionTree
    // questions contains the latest user edits (including nested questions)
    // questionTree is from Redux (backend data, may be stale)
    if (questions && questions.length > 0) {
      return buildQuestionTreeFromFlat(questions);
    }

    // Fallback to questionTree: flatten it first, then build tree
    if (
      questionTree &&
      Array.isArray(questionTree) &&
      questionTree.length > 0
    ) {
      const flattened = flattenQuestionTree(questionTree);
      return buildQuestionTreeFromFlat(flattened);
    }

    return { tree: [], allFlat: [] };
  }, [questionTree, questions, buildQuestionTreeFromFlat, flattenQuestionTree]);

  const processedQuestions = useMemo(() => {
    return processedQuestionsData.tree;
  }, [processedQuestionsData]);

  // Flatten all questions for dependsOn lookup
  // CRITICAL: Use allFlat from buildQuestionTreeFromFlat to preserve _parentQuestion references
  const allQuestionsFlat = useMemo(() => {
    return processedQuestionsData.allFlat;
  }, [processedQuestionsData]);

  const stepMap = useMemo(() => {
    return getQuestionsByStep(processedQuestions, localAnswers, allQuestionsFlat);
  }, [processedQuestions, localAnswers, allQuestionsFlat]);

  const sortedSteps = useMemo(() => {
    if (!stepMap) return [];
    return getSortedSteps(stepMap);
  }, [stepMap]);

  const currentStepQuestions = useMemo(() => {
    if (showAllQuestions || sortedSteps.length === 0) {
      return processedQuestions;
    }
    const step = sortedSteps[currentStep] ?? sortedSteps[0] ?? 0;
    return getQuestionsForStep(processedQuestions, step, localAnswers, allQuestionsFlat);
   }, [processedQuestions, localAnswers, currentStep, sortedSteps, showAllQuestions, allQuestionsFlat]);

  const questionsToRender = useMemo(() => {
    if (showAllQuestions || sortedSteps.length === 0) {
      return processedQuestions;
    }
    const step = sortedSteps[currentStep] ?? sortedSteps[0] ?? 0;
    return getQuestionsForStep(processedQuestions, step, localAnswers, allQuestionsFlat);
  }, [
    processedQuestions,
    showAllQuestions,
    sortedSteps,
    currentStep,
    localAnswers,
    allQuestionsFlat,
  ]);

  // localAnswersRef'i her değişimde güncelle (debounce callback için)
  useEffect(() => {
    localAnswersRef.current = localAnswers;
  }, [localAnswers]);

  // answers prop değiştiğinde localAnswers'ı sync et
  // KRITIK: Kullanıcı aktif yazıyorsa (isUserTypingRef) senkronizasyonu atla
  // Aksi halde API yanıtı gelince kullanıcının yazdıkları üzerine yazılır
  useEffect(() => {
    if (isUserTypingRef.current) return;
    const currentAnswersStr = JSON.stringify(localAnswers);
    const newAnswersStr = JSON.stringify(answers || {});
    if (currentAnswersStr !== newAnswersStr) {
      setLocalAnswers(answers || {});
    }
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!processedQuestions || processedQuestions.length === 0) return;

    const flattenQuestions = (
      questionList,
      collected = [],
      parentRepeatableKey = null
    ) => {
      questionList.forEach((q) => {
        if (q && q.key) {
          collected.push({
            question: q,
            parentRepeatableKey: parentRepeatableKey,
          });

          if (q.answers && Array.isArray(q.answers)) {
            q.answers.forEach((answer) => {
              if (
                answer.content?.nestedQuestions &&
                Array.isArray(answer.content.nestedQuestions)
              ) {
                const repeatableKey =
                  q.repeatable && !q.parentId ? q.key : parentRepeatableKey;
                flattenQuestions(
                  answer.content.nestedQuestions,
                  collected,
                  repeatableKey
                );
              }
            });
          }
        }
      });
      return collected;
    };

    const allQuestionData = flattenQuestions(processedQuestions);
    const updates = {};
    let hasUpdates = false;
    const newInitialized = new Set(initializedQuestions);

    allQuestionData.forEach(({ question: q, parentRepeatableKey }) => {
      const isRootRepeatable = q.repeatable && !q.parentId;

      if (q.defaultValue && q.defaultValue.trim() !== "" && !isRootRepeatable) {
        if (parentRepeatableKey) {
          const repeatableValue = localAnswers[parentRepeatableKey];
          if (Array.isArray(repeatableValue)) {
            repeatableValue.forEach((itemValue, itemIndex) => {
              const compositeKey = `${parentRepeatableKey}_${itemIndex}_${q.key}`;
              const currentValue = localAnswers[compositeKey];
              // CRITICAL: Only set defaultValue if truly uninitialized (undefined/null)
              // If user has set empty string, don't override with defaultValue
              const isTrulyUninitialized =
                currentValue === undefined || currentValue === null;

              if (!initializedQuestions.has(compositeKey) && isTrulyUninitialized) {
                if (q.type === "Checkbox") {
                  const defaultValueArray = q.defaultValue
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean);
                  updates[compositeKey] = defaultValueArray;
                } else {
                  updates[compositeKey] = q.defaultValue;
                }
                hasUpdates = true;
                newInitialized.add(compositeKey);
              } else if (currentValue !== undefined && currentValue !== null) {
                // Mark as initialized even if empty string (user has interacted)
                newInitialized.add(compositeKey);
              }
            });
          }
        } else {
          const questionKey = q.key;
          const currentValue = localAnswers[questionKey];
          // CRITICAL: Only set defaultValue if truly uninitialized (undefined/null)
          // If user has set empty string, don't override with defaultValue
          const isTrulyUninitialized =
            currentValue === undefined || currentValue === null;

          if (!initializedQuestions.has(questionKey) && isTrulyUninitialized) {
            if (q.type === "Checkbox") {
              const defaultValueArray = q.defaultValue
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean);
              updates[questionKey] = defaultValueArray;
            } else {
              updates[questionKey] = q.defaultValue;
            }
            hasUpdates = true;
            newInitialized.add(questionKey);
          } else if (currentValue !== undefined && currentValue !== null) {
            // Mark as initialized even if empty string (user has interacted)
            newInitialized.add(questionKey);
          }
        }
      } else if (q.key) {
        if (parentRepeatableKey) {
          const repeatableValue = localAnswers[parentRepeatableKey];
          if (Array.isArray(repeatableValue)) {
            repeatableValue.forEach((itemValue, itemIndex) => {
              const compositeKey = `${parentRepeatableKey}_${itemIndex}_${q.key}`;
              newInitialized.add(compositeKey);
            });
          }
        } else {
          newInitialized.add(q.key);
        }
      }
    });

    if (hasUpdates) {
      setLocalAnswers((prev) => {
        const updated = { ...prev, ...updates };
        setTimeout(() => {
          if (onChange) {
            onChange(updated);
          }
        }, 0);
        return updated;
      });
      setInitializedQuestions(newInitialized);
    } else {
      const allQuestionKeys = new Set();
      allQuestionData.forEach(({ question: q, parentRepeatableKey }) => {
        if (q.key) {
          if (parentRepeatableKey) {
            const repeatableValue = localAnswers[parentRepeatableKey];
            if (Array.isArray(repeatableValue)) {
              repeatableValue.forEach((itemValue, itemIndex) => {
                const compositeKey = `${parentRepeatableKey}_${itemIndex}_${q.key}`;
                allQuestionKeys.add(compositeKey);
              });
            }
          } else {
            allQuestionKeys.add(q.key);
          }
        }
      });

      if (allQuestionKeys.size > 0) {
        const newSet = new Set([...initializedQuestions, ...allQuestionKeys]);
        if (newSet.size !== initializedQuestions.size) {
          setInitializedQuestions(newSet);
        }
      }
    }
  }, [processedQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswerChange = useCallback(
    (key, value) => {
      // Kullanıcı yazıyor: sync engelini aç, debounce'u sıfırla
      isUserTypingRef.current = true;
      clearTimeout(syncTimeoutRef.current);

      setInitializedQuestions((prev) => {
        const newSet = new Set(prev);
        newSet.add(key);
        return newSet;
      });

      setLocalAnswers((prevAnswers) => {
        const newAnswers = { ...prevAnswers };
        if (value === null || value === undefined) {
          delete newAnswers[key];
        } else {
          newAnswers[key] = value;
        }

        // 600ms sonra parent'a bildir ve typing bayrağını kaldır
        // Bu süre içinde gelen API yanıtları localAnswers'ı silmez
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
          isUserTypingRef.current = false;
          if (onChange) {
            onChange(newAnswers);
          }
        }, 600);

        return newAnswers;
      });
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    isUserTypingRef.current = false;
    clearTimeout(syncTimeoutRef.current);
    setLocalAnswers({});
    if (onChange) {
      onChange({});
    }
    if (onClear) {
      onClear();
    }
    setShowClearConfirm(false);
  }, [onChange, onClear]);

  const handleClearClick = useCallback(() => {
    const hasAnswers = Object.keys(localAnswers).length > 0;
    if (hasAnswers) {
      setShowClearConfirm(true);
    } else {
      if (onClear) {
        onClear();
      }
    }
  }, [localAnswers, onClear]);

  const renderQuestion = useCallback(
    (question, level = 0) => {
      if (!question) return null;

      const value = localAnswers[question.key];

      return (
        <Box
          key={question._id || question._tempId || question.key}
          sx={{ mb: 2 }}>
          <QuestionRenderer
            question={question}
            value={value}
            onChange={handleAnswerChange}
            answers={localAnswers}
            onNestedChange={handleAnswerChange}
            allQuestions={allQuestionsFlat}
            level={level}
          />
        </Box>
      );
    },
    [localAnswers, handleAnswerChange, allQuestionsFlat]
  );

  const progressData = useMemo(() => {
    const isQuestionAnswered = (
      question,
      allAnswers,
      parentRepeatableKey = null
    ) => {
      let questionKey;
      if (parentRepeatableKey) {
        questionKey = `${parentRepeatableKey}_${question.key}`;
      } else {
        questionKey = question.key;
      }

      const value = allAnswers[questionKey];

      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return true;
      }

      if (
        question.defaultValue !== undefined &&
        question.defaultValue !== null &&
        question.defaultValue !== ""
      ) {
        if (question.type === "Checkbox") {
          const defaultValueArray = String(question.defaultValue)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          return defaultValueArray.length > 0;
        }
        if (question.type === "RadioButton" || question.type === "DropDown") {
          return String(question.defaultValue).trim() !== "";
        }
        return String(question.defaultValue).trim() !== "";
      }

      return false;
    };

    const countQuestions = (
      questionList,
      allAnswers,
      parentRepeatableKey = null
    ) => {
      if (!questionList || !Array.isArray(questionList)) {
        return { total: 0, answered: 0 };
      }

      let total = 0;
      let answeredCount = 0;

      questionList.forEach((q) => {
        // 🔧 ÖNEMLİ DÜZELTME:
        // isActive tanımlı değilse (undefined) soruyu aktif kabul et.
        if (!q || !q.key || q.isActive === false) {
          return;
        }

        if (q.repeatable && !q.parentId) {
          const repeatableKey = q.key;
          const repeatableValue = allAnswers[repeatableKey];

          if (Array.isArray(repeatableValue) && repeatableValue.length > 0) {
            repeatableValue.forEach((itemValue, itemIndex) => {
              const compositeKey = `${repeatableKey}_${itemIndex}`;

              if (q.answers && Array.isArray(q.answers)) {
                q.answers.forEach((answer) => {
                  if (
                    answer.content?.nestedQuestions &&
                    Array.isArray(answer.content.nestedQuestions)
                  ) {
                    if (answer.value === itemValue) {
                      answer.content.nestedQuestions.forEach((nestedQ) => {
                        if (
                          nestedQ &&
                          nestedQ.key &&
                          nestedQ.isActive !== false
                        ) {
                          if (checkQuestionVisibility(nestedQ, allAnswers, allQuestionsFlat, compositeKey)) {
                            total++;
                            if (
                              isQuestionAnswered(
                                nestedQ,
                                allAnswers,
                                compositeKey
                              )
                            ) {
                              answeredCount++;
                            }

                            if (
                              nestedQ.answers &&
                              Array.isArray(nestedQ.answers)
                            ) {
                              nestedQ.answers.forEach((nestedAnswer) => {
                                if (
                                  nestedAnswer.content?.nestedQuestions &&
                                  Array.isArray(
                                    nestedAnswer.content.nestedQuestions
                                  )
                                ) {
                                  const nestedCompositeKey = `${compositeKey}_${nestedQ.key}`;
                                  const nestedAnswerValue =
                                    allAnswers[
                                      `${compositeKey}_${nestedQ.key}`
                                    ];
                                  if (
                                    nestedAnswer.value === nestedAnswerValue
                                  ) {
                                    const deepNestedCount = countQuestions(
                                      nestedAnswer.content.nestedQuestions,
                                      allAnswers,
                                      nestedCompositeKey
                                    );
                                    total += deepNestedCount.total;
                                    answeredCount += deepNestedCount.answered;
                                  }
                                }
                              });
                            }
                          }
                        }
                      });
                    }
                  }
                });
              }
            });
          }
        } else {
          if (!checkQuestionVisibility(q, allAnswers, allQuestionsFlat, parentRepeatableKey)) {
            return;
          }

          const questionKey = parentRepeatableKey
            ? `${parentRepeatableKey}_${q.key}`
            : q.key;

          total++;
          if (isQuestionAnswered(q, allAnswers, parentRepeatableKey)) {
            answeredCount++;
          }

          if (q.answers && Array.isArray(q.answers)) {
            q.answers.forEach((answer) => {
              if (
                answer.content?.nestedQuestions &&
                Array.isArray(answer.content.nestedQuestions)
              ) {
                const currentAnswerValue = allAnswers[questionKey];

                if (answer.value === currentAnswerValue) {
                  const nestedCount = countQuestions(
                    answer.content.nestedQuestions,
                    allAnswers,
                    parentRepeatableKey
                  );
                  total += nestedCount.total;
                  answeredCount += nestedCount.answered;
                }
              }
            });
          }
        }
      });

      return { total, answered: answeredCount };
    };

    // 🔧 BURADA DA localAnswers KULLANALIM
    const { total, answered } = countQuestions(
      processedQuestions,
      localAnswers
    );

    const safeTotal = total || 0;
    const safeAnswered = Math.min(answered, safeTotal);

    const progress =
      safeTotal > 0 ? Math.round((safeAnswered / safeTotal) * 100) : 0;

    return {
      totalQuestions: safeTotal,
      answeredQuestions: safeAnswered,
      progress,
    };
  }, [processedQuestions, localAnswers, allQuestionsFlat]);

  const hasUnansweredRequired = useMemo(() => {
    if (showAllQuestions) {
      const flattenQuestions = (questionList, parentRepeatableKey = null) => {
        const result = [];
        questionList.forEach((q) => {
          if (q && q.key && q.isActive !== false) {
            if (checkQuestionVisibility(q, localAnswers, allQuestionsFlat, parentRepeatableKey)) {
              result.push({ question: q, parentRepeatableKey });
            }

            if (q.repeatable && !q.parentId) {
              const repeatableKey = q.key;
              const repeatableValue = localAnswers[repeatableKey];

              if (
                Array.isArray(repeatableValue) &&
                repeatableValue.length > 0
              ) {
                repeatableValue.forEach((itemValue, itemIndex) => {
                  const compositeKey = `${repeatableKey}_${itemIndex}`;

                  if (q.answers && Array.isArray(q.answers)) {
                    q.answers.forEach((answer) => {
                      if (
                        answer.content?.nestedQuestions &&
                        Array.isArray(answer.content.nestedQuestions)
                      ) {
                        if (answer.value === itemValue) {
                          result.push(
                            ...flattenQuestions(
                              answer.content.nestedQuestions,
                              compositeKey
                            )
                          );
                        }
                      }
                    });
                  }
                });
              }
            } else {
              if (q.answers && Array.isArray(q.answers)) {
                q.answers.forEach((answer) => {
                  if (
                    answer.content?.nestedQuestions &&
                    Array.isArray(answer.content.nestedQuestions)
                  ) {
                    const currentAnswerValue = localAnswers[q.key];
                    if (answer.value === currentAnswerValue) {
                      result.push(
                        ...flattenQuestions(
                          answer.content.nestedQuestions,
                          parentRepeatableKey
                        )
                      );
                    }
                  }
                });
              }
            }
          }
        });
        return result;
      };

      const flatQuestions = flattenQuestions(processedQuestions);

      const isQuestionAnswered = (
        question,
        allAnswers,
        parentRepeatableKey = null
      ) => {
        let questionKey;
        if (parentRepeatableKey) {
          questionKey = `${parentRepeatableKey}_${question.key}`;
        } else {
          questionKey = question.key;
        }

        const value = allAnswers[questionKey];

        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return true;
        }

        if (
          question.defaultValue !== undefined &&
          question.defaultValue !== null &&
          question.defaultValue !== ""
        ) {
          if (question.type === "Checkbox") {
            const defaultValueArray = String(question.defaultValue)
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
            return defaultValueArray.length > 0;
          }
          return String(question.defaultValue).trim() !== "";
        }

        return false;
      };

      return flatQuestions.some(({ question: q, parentRepeatableKey }) => {
        if (!q.required) {
          return false;
        }
        return !isQuestionAnswered(q, localAnswers, parentRepeatableKey);
      });
    }

    const stepQuestions = currentStepQuestions;
    if (!stepQuestions || stepQuestions.length === 0) {
      return false;
    }

    const isQuestionAnswered = (
      question,
      allAnswers,
      parentRepeatableKey = null
    ) => {
      let questionKey;
      if (parentRepeatableKey) {
        questionKey = `${parentRepeatableKey}_${question.key}`;
      } else {
        questionKey = question.key;
      }

      const value = allAnswers[questionKey];

      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return true;
      }

      if (
        question.defaultValue !== undefined &&
        question.defaultValue !== null &&
        question.defaultValue !== ""
      ) {
        if (question.type === "Checkbox") {
          const defaultValueArray = String(question.defaultValue)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          return defaultValueArray.length > 0;
        }
        return String(question.defaultValue).trim() !== "";
      }

      return false;
    };

    const flattenQuestions = (questionList, parentRepeatableKey = null) => {
      const result = [];
      questionList.forEach((q) => {
        if (q && q.key && q.isActive !== false) {
          if (checkQuestionVisibility(q, localAnswers, allQuestionsFlat, parentRepeatableKey)) {
            result.push({ question: q, parentRepeatableKey });
          }

          if (q.repeatable && !q.parentId) {
            const repeatableKey = q.key;
            const repeatableValue = localAnswers[repeatableKey];

            if (Array.isArray(repeatableValue) && repeatableValue.length > 0) {
              repeatableValue.forEach((itemValue, itemIndex) => {
                const compositeKey = `${repeatableKey}_${itemIndex}`;

                if (q.answers && Array.isArray(q.answers)) {
                  q.answers.forEach((answer) => {
                    if (
                      answer.content?.nestedQuestions &&
                      Array.isArray(answer.content.nestedQuestions)
                    ) {
                      if (answer.value === itemValue) {
                        result.push(
                          ...flattenQuestions(
                            answer.content.nestedQuestions,
                            compositeKey
                          )
                        );
                      }
                    }
                  });
                }
              });
            }
          } else {
            if (q.answers && Array.isArray(q.answers)) {
              q.answers.forEach((answer) => {
                if (
                  answer.content?.nestedQuestions &&
                  Array.isArray(answer.content.nestedQuestions)
                ) {
                  const currentAnswerValue = localAnswers[q.key];
                  if (answer.value === currentAnswerValue) {
                    result.push(
                      ...flattenQuestions(
                        answer.content.nestedQuestions,
                        parentRepeatableKey
                      )
                    );
                  }
                }
              });
            }
          }
        }
      });
      return result;
    };

    const flatQuestions = flattenQuestions(stepQuestions);

    return flatQuestions.some(({ question: q, parentRepeatableKey }) => {
      if (!q.required) {
        return false;
      }
      return !isQuestionAnswered(q, localAnswers, parentRepeatableKey);
    });
  }, [processedQuestions, currentStepQuestions, localAnswers, showAllQuestions, allQuestionsFlat]);

  const handleNext = useCallback(() => {
    if (hasUnansweredRequired) {
      return;
    }

    if (currentStep < sortedSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (onNext) {
        onNext(nextStep);
      }
    }
  }, [currentStep, sortedSteps.length, hasUnansweredRequired, onNext]);

  const handleComplete = useCallback(() => {
    if (hasUnansweredRequired) {
      return;
    }

    if (onComplete) {
      if (showAllQuestions || currentStep >= sortedSteps.length - 1) {
        onComplete();
      }
    }
  }, [
    currentStep,
    sortedSteps.length,
    hasUnansweredRequired,
    onComplete,
    showAllQuestions,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      if (onPrevious) {
        onPrevious(prevStep);
      }
    }
  }, [currentStep, onPrevious]);

  const handleToggleAllQuestions = useCallback((event) => {
    setShowAllQuestions(event.target.checked);
  }, []);

  // Adım başına soru sayısını doğru hesapla (nested sorular dahil)
  const getStepQuestionCount = useCallback((stepQuestions, allAnswers) => {
    if (!stepQuestions || stepQuestions.length === 0) return 0;

    const countQuestions = (questionList, parentRepeatableKey = null) => {
      let count = 0;
      questionList.forEach((q) => {
        if (!q || !q.key || q.isActive === false) return;
        
        if (!checkQuestionVisibility(q, allAnswers, allQuestionsFlat, null)) return;

        count++;

        if (q.repeatable && !q.parentId) {
          const repeatableKey = q.key;
          const repeatableValue = allAnswers[repeatableKey];
          if (Array.isArray(repeatableValue) && repeatableValue.length > 0) {
            repeatableValue.forEach((itemValue, itemIndex) => {
              const compositeKey = `${repeatableKey}_${itemIndex}`;
              if (q.answers && Array.isArray(q.answers)) {
                q.answers.forEach((answer) => {
                  if (
                    answer.content?.nestedQuestions &&
                    Array.isArray(answer.content.nestedQuestions)
                  ) {
                    if (answer.value === itemValue) {
                      count += countQuestions(
                        answer.content.nestedQuestions,
                        compositeKey
                      );
                    }
                  }
                });
              }
            });
          }
        } else {
          if (q.answers && Array.isArray(q.answers)) {
            q.answers.forEach((answer) => {
              if (
                answer.content?.nestedQuestions &&
                Array.isArray(answer.content.nestedQuestions)
              ) {
                const currentAnswerValue = allAnswers[q.key];
                if (answer.value === currentAnswerValue) {
                  count += countQuestions(
                    answer.content.nestedQuestions,
                    parentRepeatableKey
                  );
                }
              }
            });
          }
        }
      });
      return count;
    };

    return countQuestions(stepQuestions, null);
  }, [allQuestionsFlat]);

  const currentStepInfo = useMemo(() => {
    if (showAllQuestions || !sortedSteps.length) {
      return null;
    }
    const step = sortedSteps[currentStep] ?? sortedSteps[0] ?? 0;
    const stepIndex = sortedSteps.indexOf(step);
    const questionCount = getStepQuestionCount(currentStepQuestions, localAnswers);
    return {
      step,
      stepIndex,
      totalSteps: sortedSteps.length,
      questionCount,
    };
  }, [currentStep, sortedSteps, showAllQuestions, currentStepQuestions, localAnswers, getStepQuestionCount]);

  return (
    <Box 
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        height: "100%", 
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
      }}>
      {progressData && (
        <Box sx={{ flexShrink: 0 }}>
        <WizardProgressBar
          progress={progressData.progress}
          answeredQuestions={progressData.answeredQuestions}
          totalQuestions={progressData.totalQuestions}
        />
        </Box>
      )}

      {sortedSteps.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            mb: 2,
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAllQuestions}
                onChange={handleToggleAllQuestions}
                color="primary"
              />
            }
            label={showAllQuestions ? "Tüm Sorular" : "Adım Adım"}
          />

          {!showAllQuestions && currentStepInfo && (
            <Typography variant="body2" color="text.secondary">
              Adım {currentStepInfo.stepIndex + 1}/{currentStepInfo.totalSteps}
              {currentStepInfo.questionCount > 0 && (
                <span> ({currentStepInfo.questionCount} soru)</span>
              )}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          // Modern ince scrollbar - Webkit (Chrome, Safari, Edge)
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "3px",
            transition: "background 0.2s ease",
            "&:hover": {
              background: "rgba(0, 0, 0, 0.35)",
            },
          },
          // Modern ince scrollbar - Firefox
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
        }}>
        {!processedQuestions || processedQuestions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Bu şablon için tanımlı soru bulunmamaktadır. Direkt önizlemeye geçebilirsiniz.
            </Typography>
            {onComplete && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={onComplete}
                sx={{ mt: 2 }}>
                Önizle
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {questionsToRender.map((question) => renderQuestion(question, 0))}

            <Divider sx={{ my: 3 }} />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleClearClick}
              disabled={Object.keys(localAnswers).length === 0}>
              Tüm Cevapları Temizle
            </Button>

            <ConfirmDialog
              open={showClearConfirm}
              onClose={() => setShowClearConfirm(false)}
              onConfirm={handleClear}
              title="Tüm Cevapları Temizle"
              message="Tüm test cevaplarını temizlemek istediğinizden emin misiniz? Bu işlem geri alınamaz."
              confirmText="Temizle"
              cancelText="İptal"
              severity="warning"
            />
          </Box>
        )}
      </Box>

      {sortedSteps.length > 0 && !showAllQuestions && (
        <Paper
          sx={{
            flexShrink: 0,
            p: 2,
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            zIndex: 10,
          }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handlePrevious}
            disabled={currentStep === 0}>
            Geri
          </Button>

          {hasUnansweredRequired && (
            <Typography
              variant="body2"
              color="error"
              sx={{ alignSelf: "center", flex: 1, textAlign: "center" }}>
              Lütfen zorunlu soruları cevaplayın
            </Typography>
          )}

          {currentStep >= sortedSteps.length - 1 && onComplete ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleComplete}
              disabled={hasUnansweredRequired}>
              Önizle
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={
                currentStep >= sortedSteps.length - 1 || hasUnansweredRequired
              }>
              İleri
            </Button>
          )}
        </Paper>
      )}

      {showAllQuestions && onComplete && (
        <Paper
          sx={{
            flexShrink: 0,
            p: 2,
            mt: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            zIndex: 10,
          }}>
          {hasUnansweredRequired && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center" }}>
              Lütfen tüm zorunlu soruları cevaplayın
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleComplete}
            disabled={hasUnansweredRequired}>
            Önizle
          </Button>
        </Paper>
      )}
    </Box>
  );
};

AnswerForm.propTypes = {
  questions: PropTypes.array,
  questionTree: PropTypes.array,
  answers: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  showAllQuestions: PropTypes.bool,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  onComplete: PropTypes.func,
};

AnswerForm.defaultProps = {
  questions: [],
  questionTree: null,
  answers: {},
  onClear: null,
  showAllQuestions: false,
  onNext: null,
  onPrevious: null,
  onComplete: null,
};

export default AnswerForm;
